import { useState } from "react";
import { ShieldAlert, AlertTriangle, Loader2, Check, ListChecks } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Iter 74 — Panel "Reiniciar datos para nueva importación".
 *
 * Borra TODAS las colecciones operativas (clientes, reparaciones, inventario,
 * presupuestos, facturas, albaranes, abonos, movimientos de stock, checklists
 * y firmas) y resetea los contadores correlativos a 1.
 *
 * Conserva intactos: usuarios, roles, configuración de empresa, Google,
 * plantillas de email/PDF, diseños y todos los ajustes.
 *
 * Antes de borrar, crea automáticamente un backup completo descargable
 * desde Configuración → Backups (BackupTab).
 *
 * Sólo se muestra a admins (la ruta /settings ya está protegida con
 * RoleGate roles=["admin"]).
 */
const KEEP_LIST = [
  "Usuarios, roles y permisos",
  "Configuración de empresa",
  "Conexión Google (Calendar + Gmail)",
  "Técnicos y sus calendarios",
  "Plantillas HTML5 de email",
  "Diseños de PDFs y documentos",
  "Configuración de facturación (prefijos, IVA, plazos)",
  "Categorías de inventario configurables",
  "Garantías y opciones desplegables configurables",
];

const CLEAR_LIST = [
  "Clientes",
  "Reparaciones (con su timeline e historial)",
  "Inventario (productos y servicios)",
  "Presupuestos",
  "Facturas, albaranes y abonos",
  "Movimientos de stock",
  "Checklists asociados a reparaciones",
  "Firmas digitales y tokens de firma",
  "Gastos operativos",
  "Historial de importaciones previas",
];

export default function ResetDataPanel({ onReset }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const CONFIRM_TOKEN = "BORRAR DATOS";

  const close = () => {
    setOpen(false);
    setConfirm("");
    setResult(null);
  };

  const execute = async () => {
    if (confirm !== CONFIRM_TOKEN) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("confirm", CONFIRM_TOKEN);
      const r = await api.post("/admin/reset-operational-data", fd);
      setResult(r.data);
      toast.success("Datos operativos reiniciados correctamente.");
      onReset?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border-2 border-red-300 p-5" data-testid="reset-data-panel">
      <div className="flex items-start gap-3">
        <div className="shrink-0 mt-1">
          <ShieldAlert size={28} className="text-red-600" />
        </div>
        <div className="flex-1">
          <div className="font-display font-bold text-base text-red-700">
            Reiniciar datos para nueva importación
          </div>
          <p className="text-sm text-[#525252] mt-1">
            Borra <b>todos los datos operativos actuales</b> y deja los contadores
            (factura, albarán, reparación, presupuesto) a 1. Antes del borrado se
            genera automáticamente un <b>backup completo</b>, accesible y descargable
            desde <span className="font-mono">Configuración → Backups</span>.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="border border-red-200 bg-red-50 p-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-red-700 font-semibold mb-2 inline-flex items-center gap-1.5">
                <AlertTriangle size={11} /> Se borrará
              </div>
              <ul className="text-xs text-[#525252] space-y-1 list-disc pl-4">
                {CLEAR_LIST.map((t) => <li key={t}>{t}</li>)}
              </ul>
            </div>
            <div className="border border-[#6E8B3D] bg-[#F4F8EC] p-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#6E8B3D] font-semibold mb-2 inline-flex items-center gap-1.5">
                <Check size={11} /> Se conservará intacto
              </div>
              <ul className="text-xs text-[#525252] space-y-1 list-disc pl-4">
                {KEEP_LIST.map((t) => <li key={t}>{t}</li>)}
              </ul>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="reset-data-open-btn"
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider bg-red-600 text-white hover:bg-red-700 font-semibold"
          >
            <ShieldAlert size={14} /> Reiniciar datos para nueva importación
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={close}>
          <div
            className="bg-white max-w-lg w-full p-6 border-2 border-red-500"
            onClick={(e) => e.stopPropagation()}
            data-testid="reset-data-modal"
          >
            {!result ? (
              <>
                <div className="flex items-center gap-2 text-red-700 font-display font-bold text-lg">
                  <ShieldAlert size={20} /> Confirmar reinicio
                </div>
                <p className="text-sm text-[#525252] mt-3">
                  Esta acción <b>borrará todos los datos operativos</b> de forma irreversible
                  (clientes, reparaciones, inventario, facturas, etc.) y reseteará los contadores.
                </p>
                <p className="text-sm text-[#525252] mt-2">
                  Se generará un <b>backup completo</b> automáticamente antes del borrado.
                  Podrás descargarlo desde <span className="font-mono">Configuración → Backups</span>.
                </p>
                <div className="mt-4">
                  <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                    Escribe exactamente <b className="text-red-700">{CONFIRM_TOKEN}</b> para confirmar:
                  </label>
                  <input
                    type="text"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoFocus
                    data-testid="reset-data-confirm-input"
                    className="mt-1 w-full border border-red-300 focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none px-3 py-2 font-mono text-sm"
                    placeholder={CONFIRM_TOKEN}
                  />
                </div>
                <div className="mt-5 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={close}
                    data-testid="reset-data-cancel"
                    className="px-4 py-2 text-xs uppercase tracking-wider border border-[#E5E5E0] hover:bg-[#F7F7F4]"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={execute}
                    disabled={confirm !== CONFIRM_TOKEN || busy}
                    data-testid="reset-data-execute"
                    className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider bg-red-600 text-white hover:bg-red-700 font-semibold disabled:bg-[#A8A8A0] disabled:cursor-not-allowed"
                  >
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldAlert size={14} />}
                    {busy ? "Procesando…" : "Borrar datos"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 text-[#6E8B3D] font-display font-bold text-lg">
                  <Check size={20} /> Reinicio completado
                </div>
                <p className="text-sm text-[#525252] mt-3">
                  Los datos operativos se han borrado correctamente y los contadores se han reseteado a <b>1</b>.
                </p>
                <div className="mt-3 bg-[#F4F8EC] border border-[#6E8B3D] p-3 text-xs">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#6E8B3D] font-semibold mb-1">
                    Backup automático generado
                  </div>
                  <div className="font-mono text-[11px] break-all" data-testid="reset-data-snapshot-name">
                    {result.snapshot}
                  </div>
                  <div className="text-[11px] text-[#525252] mt-1">
                    Disponible en Configuración → Backups.
                  </div>
                </div>
                {result.cleared && (
                  <div className="mt-3">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-1 inline-flex items-center gap-1">
                      <ListChecks size={11} /> Detalle por colección
                    </div>
                    <div className="border border-[#E5E5E0] max-h-48 overflow-y-auto">
                      <table className="w-full text-xs">
                        <tbody>
                          {Object.entries(result.cleared).map(([k, v]) => (
                            <tr key={k} className="border-b border-[#E5E5E0]">
                              <td className="p-1.5 font-mono">{k}</td>
                              <td className="p-1.5 text-right font-bold">{v}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                <div className="mt-5 flex justify-end">
                  <button
                    type="button"
                    onClick={close}
                    data-testid="reset-data-close"
                    className="px-4 py-2 text-xs uppercase tracking-wider bg-[#111110] text-white hover:bg-[#2a2a28]"
                  >
                    Cerrar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
